import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, RefreshCw, Wrench, UserCog, Shield, Crown, Users, ExternalLink } from "lucide-react";

// =============================================================================
// WORKFLOW — visualize the surveydrafts.us assignment pipeline
//   Field Crew → Drafter → QA/QC → Final QC → Client
// For each stage, show:
//   - members across all vendors that hold this role
//   - count of jobs currently sitting in that stage (live)
// Drill into a stage to see member contribution by vendor.
// =============================================================================

const STAGE_ICON = {
  fieldcrew:    Wrench,
  drafter:      UserCog,
  qaqc:         Shield,
  finalqc:      Shield,
  team_manager: Crown,
};
const STAGE_TINT = {
  fieldcrew: "bg-sky-50 border-sky-300",
  drafter:   "bg-yellow-50 border-yellow-400",
  qaqc:      "bg-indigo-50 border-indigo-400",
  finalqc:   "bg-purple-50 border-purple-400",
};

// Pipeline order — uses Land Drafters stage keys (which `_sd_upsert_one` maps to)
const PIPELINE = [
  { key: "fieldcrew", label: "Field Crew", stage_key: "in_field" },
  { key: "drafter",   label: "Drafter",    stage_key: "drafter" },
  { key: "qaqc",      label: "QA / QC",    stage_key: "qa1" },
  { key: "finalqc",   label: "Final QC",   stage_key: "final_qc" },
];

export default function Workflow() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/integrations/surveydrafts/hierarchy")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const onSync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/jobs/sync-surveydrafts-extended");
      if (data.ok === false) toast.error(data.reason || "Sync failed");
      else { toast.success("Pipeline refreshed"); load(); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    }
    setSyncing(false);
  };

  // Build per-stage rollup across all vendors
  const stages = useMemo(() => {
    if (!data) return [];
    return PIPELINE.map((s) => {
      let memberCount = 0;
      let jobCount = 0;
      const byVendor = [];
      for (const v of data.vendors) {
        const role = v.roles.find((r) => r.key === s.key);
        const memberN = role ? role.count : 0;
        const jobN = v.stage_counts?.[s.stage_key] || 0;
        memberCount += memberN;
        jobCount += jobN;
        if (memberN || jobN) {
          byVendor.push({
            vendor: v.vendor,
            members: role?.members || [],
            jobs: jobN,
          });
        }
      }
      return { ...s, memberCount, jobCount, byVendor };
    });
  }, [data]);

  return (
    <div className="space-y-6 animate-fade-up" data-testid="workflow-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">ASSIGNMENT CYCLE</div>
          <h1 className="font-heading text-4xl font-black tracking-tight leading-none mt-1">Workflow</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            The surveydrafts.us pipeline · click a stage to see team-level breakdown
          </p>
        </div>
        <button
          onClick={onSync}
          disabled={syncing}
          className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-emerald-300 hover:bg-emerald-400 disabled:opacity-60"
          data-testid="workflow-sync-btn"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "SYNCING…" : "REFRESH"}
        </button>
      </div>

      {loading && <div className="font-mono text-xs text-zinc-500">Loading workflow…</div>}

      {!loading && data && (
        <>
          {/* Pipeline tiles */}
          <div className="flex items-stretch gap-2 overflow-x-auto pb-4" data-testid="workflow-pipeline">
            {stages.map((s, idx) => (
              <React.Fragment key={s.key}>
                <StageTile
                  s={s}
                  active={selectedStage === s.key}
                  onClick={() => setSelectedStage(selectedStage === s.key ? null : s.key)}
                />
                {idx < stages.length - 1 && (
                  <div className="flex items-center text-zinc-400 shrink-0 px-1" aria-hidden>
                    <ArrowRight className="w-5 h-5" />
                  </div>
                )}
              </React.Fragment>
            ))}
            {/* Terminal node — Client */}
            <div className="flex items-center text-zinc-400 shrink-0 px-1" aria-hidden>
              <ArrowRight className="w-5 h-5" />
            </div>
            <div className="brut-card p-4 shrink-0 min-w-[180px] bg-emerald-100 border-emerald-400 flex flex-col items-center justify-center">
              <ExternalLink className="w-5 h-5 mb-1" />
              <div className="font-heading text-lg font-black tracking-tight">Client</div>
              <div className="label-mono text-zinc-600 text-[10px]">SENT / DELIVERED</div>
            </div>
          </div>

          {/* Drill-down — selected stage by vendor */}
          {selectedStage && (
            <StageBreakdown
              stage={stages.find((s) => s.key === selectedStage)}
              onClose={() => setSelectedStage(null)}
            />
          )}
        </>
      )}
    </div>
  );
}

function StageTile({ s, active, onClick }) {
  const Icon = STAGE_ICON[s.key] || Users;
  const tint = STAGE_TINT[s.key] || "bg-zinc-50";
  return (
    <button
      onClick={onClick}
      className={`brut-card p-4 shrink-0 min-w-[220px] text-left transition-all ${tint} ${
        active ? "ring-2 ring-zinc-900 shadow-lg" : "hover:shadow-md"
      }`}
      data-testid={`workflow-stage-${s.key}`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4" />
        <div className="font-heading text-base font-black">{s.label}</div>
      </div>
      <div className="grid grid-cols-2 gap-2 text-center">
        <div>
          <div className="label-mono text-zinc-500">PEOPLE</div>
          <div className="font-heading text-2xl font-black">{s.memberCount}</div>
        </div>
        <div>
          <div className="label-mono text-zinc-500">IN STAGE</div>
          <div className="font-heading text-2xl font-black">{s.jobCount.toLocaleString()}</div>
        </div>
      </div>
    </button>
  );
}

function StageBreakdown({ stage, onClose }) {
  if (!stage) return null;
  return (
    <div className="brut-card p-5 space-y-4" data-testid="workflow-breakdown">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-mono">STAGE BREAKDOWN</div>
          <div className="font-heading text-2xl font-black">{stage.label}</div>
        </div>
        <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 text-xs font-mono">CLOSE ×</button>
      </div>
      {stage.byVendor.length === 0 ? (
        <div className="font-mono text-xs text-zinc-500 py-6 text-center">
          No vendors with members in this role yet.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {stage.byVendor.map((row) => (
            <div key={row.vendor.surveydrafts_id || row.vendor.name} className="border border-zinc-900 bg-zinc-50 p-3" data-testid={`workflow-vendor-${row.vendor.surveydrafts_id || row.vendor.name}`}>
              <div className="flex items-center justify-between gap-2 mb-2">
                <div className="font-bold text-sm truncate" title={row.vendor.name}>{row.vendor.name}</div>
                <Link
                  to={row.vendor.surveydrafts_id
                    ? `/jobs?vendor_id=${encodeURIComponent(row.vendor.surveydrafts_id)}&vendor_name=${encodeURIComponent(row.vendor.name || "")}`
                    : "/jobs"}
                  className="text-[10px] font-mono underline text-emerald-700"
                >
                  {row.jobs} jobs →
                </Link>
              </div>
              <div className="space-y-1">
                {row.members.length === 0 && (
                  <div className="text-[10px] font-mono text-zinc-500">No assigned members.</div>
                )}
                {row.members.map((m) => (
                  <div key={m.id} className="text-xs flex items-center gap-1.5 font-mono truncate" title={m.email}>
                    <span className="w-1.5 h-1.5 rounded-full bg-zinc-700 shrink-0" />
                    {m.name}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
